/* Route parsing, pure and tested. Keeps the live app's URL contract so links already in the
   world (Dad's reveal, short words, the party-prep link) keep working on the new build. */
export type Route =
  | { kind: "landing" }
  | { kind: "create" }
  | { kind: "account" }
  | { kind: "office" }
  | { kind: "unsubscribe" }
  | { kind: "help" }
  | { kind: "privacy" }
  | { kind: "terms" }
  | { kind: "about" }
  | { kind: "teams" }
  | { kind: "gift" }
  | { kind: "occasions" }
  | { kind: "occasion"; slug: string; later?: boolean }
  | { kind: "compare"; slug: string }
  | { kind: "guide"; slug: string }
  | { kind: "optin"; which: "optin" | "remind" }
  | { kind: "textreminders" }
  | { kind: "latercreate" }
  | { kind: "laterpage" }
  | { kind: "latergift" }
  | { kind: "laterreport" }
  | { kind: "keepsake"; slug: string; token: string }
  | { kind: "verify"; slug: string; token: string }
  | { kind: "status" }
  | { kind: "submit"; slug: string }
  | { kind: "admin"; slug: string }
  | { kind: "reaction"; slug: string }
  | { kind: "edit"; slug: string; token: string }
  | { kind: "remote"; slug: string; token: string }
  | { kind: "auth"; to: string }
  | { kind: "reveal"; slug: string; token: string; trailerOnly: boolean; prep: boolean }
  | { kind: "dev"; which: string };

/* Words no page or short link may take, because the router (or Netlify) answers them first.
   Mirrored on the server in create_page (0032) — tests/routes.test.ts checks the two agree. */
export const RESERVED_LIST = ["admin", "api", "app", "apps", "assets", "auth", "beta", "blog", "create", "css", "demo", "docs",
  "help", "home", "img", "index", "js", "keepsake", "keepsakepage", "login", "new", "page", "pages", "privacy",
  "reveal", "signin", "signup", "static", "support", "terms", "www", "unsubscribe", "status", "about", "contact", "teams",
  "pricing", "faq", "legal", "settings", "account", "office", "phone", "favicon", "gift", "optin", "remind"];   // optin + remind (0149): the one-tap links in the thank-you and the milestone reminder   // "text-reminders" is routed before any slug (routes.ts) but NOT reserved here: the server list (create_page) is the mirror and this list must match it
export const RESERVED = new Set(RESERVED_LIST);

/* Only same-origin destinations: a path (with hash), never another site. Browsers read "\\" as "/" in URLs, so
   "/\\evil.com" (or its %5C encoding) would have passed a plain leading-slash test and left the site (2026-10-10 audit):
   resolve against our origin and refuse anything whose origin differs or that starts with "//" or "/\\". */
export function safeTo(to: string | null): string {
  const t = (to || "").trim();
  if (!/^\/[^/\\]/.test(t) && t !== "/") return "/app";
  try { const u = new URL(t, "https://keepsakepage.com"); if (u.origin !== "https://keepsakepage.com") return "/app"; return u.pathname + u.search + u.hash; } catch { return "/app"; }
}

/* The live app's short links (Netlify redirects like /pops80) landed on /word?reveal=<token>; the
   invitations that were printed with them still do. Fold that into the hash form the app uses.
   Returns the tidy URL, or null when the location is already fine. Pure. */
export function legacyRewrite(loc: { pathname: string; hash: string; search: string }): string | null {
  const path = loc.pathname.replace(/^\/+|\/+$/g, "");
  if (!path || path.includes("/") || RESERVED.has(path) || (loc.hash || "").length > 1) return null;
  const q = new URLSearchParams(loc.search || "");
  const kind = q.has("reveal") ? "reveal" : q.has("celebrate") ? "celebrate" : null;
  const tok = kind ? (q.get(kind) || "").trim() : "";
  if (!kind || !tok) return null;
  const trailer = q.get("trailer") === "1"; const prep = q.get("prep") === "1";
  return "/" + path + (prep ? "?prep=1" : "") + "#" + kind + "=" + encodeURIComponent(tok) + (trailer ? "&trailer=1" : "");
}

export function parseLocation(loc: { pathname: string; hash: string; search: string }): Route {
  const path = loc.pathname.replace(/^\/+|\/+$/g, "");
  const hash = (loc.hash || "").replace(/^#/, "");
  const q = new URLSearchParams(loc.search || "");
  if (!path) return hash.startsWith("create") ? { kind: "create" } : { kind: "landing" };
  if (path === "app") return hash.startsWith("office") ? { kind: "office" } : { kind: "account" };
  if (path === "auth/callback") return { kind: "auth", to: safeTo(q.get("to")) };
  if (path === "unsubscribe") return { kind: "unsubscribe" };
  if (path === "optin" || path === "remind") return { kind: "optin", which: path };   // the signed one-tap links (0149)
  if (path === "help") return { kind: "help" };
  if (path === "privacy") return { kind: "privacy" };
  if (path === "terms") return { kind: "terms" };
  if (path === "about") return { kind: "about" };
  if (path === "teams") return { kind: "teams" };
  if (path === "gift") return { kind: "gift" };                                 // the buyable Keepsakes-for-later gift page (SEO plan "Gift"); reserved on the server too (0095)
  if (path === "text-reminders") return { kind: "textreminders" };
  if (path === "occasions") return { kind: "occasions" };                       // the occasion landing pages (SEO plan 2026-10-14); routed before any slug like text-reminders, not in RESERVED_LIST
  if (path.startsWith("occasions/")) return { kind: "occasion", slug: path.slice("occasions/".length) };
  if (path.startsWith("compare/")) return { kind: "compare", slug: path.slice("compare/".length) };   // /compare/<slug> (feature flag VITE_COMPARE_PAGES; the page itself answers "not here yet" when off)
  if (path.startsWith("guides/")) return { kind: "guide", slug: path.slice("guides/".length).toLowerCase() };   // /guides/<slug> — the Content engine's published pieces (0148; prerendered at build from site_content_live)
  // Keepsakes for later (routed before any slug, like text-reminders; NOT in RESERVED_LIST — the server list is the mirror). The
  // older /open-when* spellings still answer (links already sent), mapped to the same screens.
  if (path === "later/report") return { kind: "laterreport" };
  if (path === "later/gift" || path === "open-when/gift") return { kind: "latergift" };
  if (path === "later/start" || path === "open-when/start") return { kind: "latercreate" };
  if (path === "later" || path === "open-when") return q.has("gift") || q.has("code") ? { kind: "latercreate" } : { kind: "laterpage" };
  if (path.startsWith("later/")) return { kind: "occasion", slug: path.slice("later/".length), later: true };   // a Keepsakes-for-later occasion page (/later/new-baby); report/gift/start are routed above   // a gift/invite link lands straight on the form (the emails use /later?gift=CODE)   // routed before any slug, like text-reminders (not in RESERVED_LIST: the server list is the mirror)
  if (path === "status") return { kind: "status" };
  if (path.startsWith("_dev/")) return { kind: "dev", which: path.slice(5) };
  const slug = path.split("/")[0];
  if (hash.startsWith("admin")) return { kind: "admin", slug };
  if (hash.startsWith("reaction")) return { kind: "reaction", slug };
  const e = hash.match(/^edit=([A-Za-z0-9]+)/); if (e) return { kind: "edit", slug, token: e[1] };
  const rm = hash.match(/^remote=([A-Za-z0-9]+)/); if (rm) return { kind: "remote", slug, token: rm[1] };
  const fr = hash.match(/^for=([A-Za-z0-9]+)/); if (fr) return { kind: "keepsake", slug, token: fr[1] };   // a keepsake recipient's private link
  const vf = hash.match(/^verify=([A-Za-z0-9]+)/); if (vf) return { kind: "verify", slug, token: vf[1] };   // a trusted contact's claim link
  const m = hash.match(/^(reveal|celebrate)=([^&]+)(.*)$/);
  if (m) return { kind: "reveal", slug, token: decodeURIComponent(m[2]), trailerOnly: /(^|&)trailer=1(&|$)/.test(m[3] || ""), prep: q.get("prep") === "1" };
  // the old query form, in case it reaches the router un-rewritten
  const qt = (q.get("reveal") || q.get("celebrate") || "").trim();
  if (qt && !hash) return { kind: "reveal", slug, token: qt, trailerOnly: q.get("trailer") === "1", prep: q.get("prep") === "1" };
  return { kind: "submit", slug };
}
